import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import {
  lastActivity, getIdleTimeoutMinutes, hasHolds, sendHeartbeat, recordActivity, dispatchSessionEnded
} from '../../services/sessionManager';

const WARN_BEFORE_MS = 2 * 60 * 1000;

function formatRemaining(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Warns before an inactivity sign-out (security hardening batch 2, 30 Sep 2026).
 * The server decides when a session ends; this only gives the person a chance to stay
 * signed in, and returns them to the login page when the time is up.
 */
export default function SessionTimeoutGuard({ onSignOut }) {
  const [remainingMs, setRemainingMs] = useState(null);

  useEffect(() => {
    // Learn this session's idle timeout.
    sendHeartbeat();
    const timer = setInterval(() => {
      const minutes = getIdleTimeoutMinutes();
      if (!minutes || hasHolds()) {
        setRemainingMs(null);
        return;
      }
      const left = minutes * 60 * 1000 - (Date.now() - lastActivity());
      if (left <= 0) {
        setRemainingMs(null);
        dispatchSessionEnded('SESSION_EXPIRED');
      } else if (left <= WARN_BEFORE_MS) {
        setRemainingMs(left);
      } else {
        setRemainingMs(null);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const stay = async () => {
    recordActivity();
    setRemainingMs(null);
    await sendHeartbeat();
  };

  return (
    <Dialog open={remainingMs !== null} aria-labelledby="session-timeout-title">
      <DialogTitle id="session-timeout-title">Are you still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          To keep your information safe, you will be signed out in {formatRemaining(remainingMs || 0)} because
          there has been no activity.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSignOut}>Sign out</Button>
        <Button variant="contained" onClick={stay} autoFocus>Stay signed in</Button>
      </DialogActions>
    </Dialog>
  );
}
